/**
 * NLRuleInput — Natural language rule entry with example scaffolds.
 *
 * UX Spec Section 5.2.4 / Story 1.5 AC#1.
 *
 * States: empty, focused, typing, submitting, reflected, disabled.
 * Enter submits (single line), Shift+Enter for newline.
 */

import { useState, useRef, useCallback } from "react";
import { Loader2, Sparkles } from "lucide-react";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type NLRuleInputState =
  | "empty"
  | "focused"
  | "typing"
  | "submitting"
  | "reflected"
  | "disabled";

export interface NLRuleInputProps {
  onSubmit: (ruleText: string) => void;
  state?: NLRuleInputState;
  /** Whether LLM is available (from CapabilityManifest) */
  nlAvailable?: boolean;
  /** Inline (below grid) or expanded (full-width) variant */
  variant?: "inline" | "expanded";
  /**
   * Example prompts shown under "Try an example". Callers should build these
   * from the connected engagement's real table/column names (schema metadata
   * only — never sample values) so the scaffolds feel grounded rather than
   * generic. Falls back to domain-agnostic defaults when the caller has no
   * profiled schema yet (e.g. panel opened before any run completes).
   */
  examples?: string[];
  /**
   * When set the user's role may not use natural-language authoring (it needs
   * `rules:execute`): the input is replaced by a notice carrying this text
   * (e.g. "Requires the rules:execute permission"). Distinct from
   * `nlAvailable=false`, which means the deployment has no LLM.
   */
  blockedReason?: string | null;
}

// ---------------------------------------------------------------------------
// Example scaffolds
// ---------------------------------------------------------------------------

const DEFAULT_EXAMPLE_RULES = [
  "Every claim must have a valid member_id that exists in the Members table",
  "Order dates should never be in the future",
  "Total amount must equal quantity times unit price, within 1% tolerance",
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function NLRuleInput({
  onSubmit,
  state: controlledState,
  nlAvailable = true,
  variant = "inline",
  examples = DEFAULT_EXAMPLE_RULES,
  blockedReason = null,
}: NLRuleInputProps) {
  const [internalState, setInternalState] = useState<NLRuleInputState>("empty");
  const [text, setText] = useState("");
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const state = controlledState ?? internalState;
  const isDisabled =
    state === "disabled" || state === "submitting" || !nlAvailable;

  const handleSubmit = useCallback(() => {
    const trimmed = text.trim();
    if (!trimmed || isDisabled) return;
    // Do NOT also set internalState here: the single caller of this
    // component drives "submitting" via the controlled `state` prop, and
    // mutating internalState too would leave it stuck at "submitting"
    // forever once the controlled prop reverts to undefined — the textarea
    // would stay permanently disabled (isDisabled reads whichever of the
    // two is active), and a disabled textarea can never fire the onChange
    // that would otherwise reset internalState. Real bug, seen live: the
    // NL input locked up after a successful confirm.
    onSubmit(trimmed);
  }, [text, isDisabled, onSubmit]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleExampleClick = (example: string) => {
    setText(example);
    setInternalState("typing");
    inputRef.current?.focus();
  };

  // Role may not use NL authoring: say so, and point at the builder form.
  if (blockedReason) {
    return (
      <div className="rounded-xl glass-surface p-4" role="note">
        <div className="flex items-center gap-2 text-[var(--warning)] mb-2">
          <Sparkles className="w-4 h-4" />
          <span className="text-body font-medium">
            Describing rules in English is unavailable
          </span>
        </div>
        <p className="text-caption text-[var(--muted-foreground)]">
          {blockedReason}. Ask a project administrator to grant it.
        </p>
      </div>
    );
  }

  // Deterministic mode banner
  if (!nlAvailable) {
    return (
      <div className="rounded-xl glass-surface p-4">
        <div className="flex items-center gap-2 text-[var(--warning)] mb-2">
          <Sparkles className="w-4 h-4" />
          <span className="text-body font-medium">Deterministic Mode</span>
        </div>
        <p className="text-caption text-[var(--muted-foreground)]">
          Natural language rules unavailable in this deployment. Use the rule
          builder form below.
        </p>
      </div>
    );
  }

  return (
    <div className={`${variant === "expanded" ? "max-w-2xl mx-auto" : ""}`}>
      {/* Prompt */}
      <p className="text-body text-[var(--muted-foreground)] mb-2">
        You know this data better than anyone. What do you usually eyeball when
        you check data?
      </p>

      {/* Input area */}
      <div className="relative">
        <textarea
          ref={inputRef}
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            setInternalState(e.target.value ? "typing" : "focused");
          }}
          onFocus={() => !text && setInternalState("focused")}
          onBlur={() => !text && setInternalState("empty")}
          onKeyDown={handleKeyDown}
          disabled={isDisabled}
          placeholder="Describe what good data looks like..."
          aria-label="Enter a data quality rule in plain English"
          aria-describedby="nl-rule-examples"
          rows={variant === "expanded" ? 4 : 2}
          className={`
            w-full px-4 py-3 rounded-xl glass-surface-input
            text-body text-[var(--foreground)]
            placeholder:text-[var(--muted-foreground)]
            outline-none resize-none
            transition-all duration-200
            ${
              state === "focused" || state === "typing"
                ? "ring-2 ring-[var(--primary)]"
                : "ring-1 ring-[var(--border)]"
            }
            ${state === "empty" ? "animate-pulse-subtle" : ""}
            disabled:opacity-50 disabled:cursor-not-allowed
          `}
        />

        {/* Submit button */}
        {text.trim() && (
          <button
            onClick={handleSubmit}
            disabled={isDisabled}
            className="absolute right-3 bottom-3 px-3 py-1.5 rounded-lg bg-[var(--primary)] text-[var(--primary-foreground)] text-caption font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
          >
            {state === "submitting" ? (
              <span className="flex items-center gap-1">
                <Loader2 className="w-3 h-3 animate-spin" />
                Interpreting…
              </span>
            ) : (
              "Interpret Rule"
            )}
          </button>
        )}
      </div>

      {/* Example scaffolds */}
      {(state === "empty" || state === "focused") && (
        <div id="nl-rule-examples" className="mt-3 space-y-1.5">
          <p className="text-caption text-[var(--muted-foreground)]">
            Try an example:
          </p>
          {examples.map((example, i) => (
            <button
              key={i}
              onClick={() => handleExampleClick(example)}
              className="block w-full text-left px-3 py-2 rounded-lg text-caption text-[var(--muted-foreground)] hover:bg-white/5 hover:text-[var(--foreground)] transition-colors"
            >
              "{example}"
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
